import { useMemo, useEffect, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import {
    createZodFormResolver,
    FormSelectField,
    FormStepperField,
    getDefaults,
    lt,
    SubmitHandler,
    useFormControls,
    DateTimeUtcService,
    useAppClockService,
} from '@mews/framework';
import { WithScope } from '@mews-commander/types';
import { DateTimeField } from '@mews-commander/form';
import { DropdownItem } from '@optimus-web/core';
import { Product, Reservation } from '@mews-commander/data-access';
import { getCurrentEnterpriseReservationsByIds } from '@mews-commander/legacy-entities/src/selectors/reservations';
import { useCommanderSelector } from '@mews-commander/utils';
import { FormLayout } from '../../FormLayout';
import { AddProductFormCardFormKeys, AddProductFormCardFormValues, useAddProductFormCardSchema } from './types';
import { useAddProductOrderMutation } from './useAddProductOrderMutation';
import { ProductOptions, useGetProductOptions } from './useGetProductOptions';
import { checkIsDateTimeVisible } from './util';

interface AddProductFormCardProps extends WithScope {
    reservationIds: string[];
    onSuccess?: () => void;
    serviceId: string;
}

export const AddProductFormCard = ({ scope, onSuccess, reservationIds, serviceId }: AddProductFormCardProps) => {
    const { mutate, isPending } = useAddProductOrderMutation({
        reservationIds,
    });

    const onSubmit: SubmitHandler<AddProductFormCardFormValues> = (data, { reset }) => {
        mutate(data, {
            onSuccess: () => {
                onSuccess?.();
                reset();
            },
        });
    };

    const { productOptions, isProductOptionsLoading, serviceProducts } = useGetProductOptions({
        serviceId,
        scope,
    });

    const reservations = useCommanderSelector((state) =>
        getCurrentEnterpriseReservationsByIds(state, { ids: reservationIds })
    );

    return (
        <AddProductFormCardCore
            scope={scope}
            onSubmit={onSubmit}
            isLoading={isPending || isProductOptionsLoading}
            productOptions={productOptions}
            serviceProducts={serviceProducts}
            reservations={reservations}
        />
    );
};

interface AddProductFormCardCoreProps extends WithScope {
    onSubmit: SubmitHandler<AddProductFormCardFormValues>;
    isLoading?: boolean;
    productOptions: ProductOptions;
    serviceProducts: Product[];
    reservations: Reservation[];
}

const AddProductFormCardCore = ({
    onSubmit,
    scope,
    isLoading,
    productOptions,
    serviceProducts,
    reservations,
}: AddProductFormCardCoreProps) => {
    const schema = useAddProductFormCardSchema(serviceProducts);

    const { renderFormContent } = useFormControls({
        onSubmit,
        resolver: createZodFormResolver(schema),
        defaultValues: getDefaults(schema) as AddProductFormCardFormValues,
        dataTestForm: 'add-product-form',
        id: `add-product-form-${scope}`,
    });

    return renderFormContent(
        <FormLayout vertical loading={isLoading} submitButtonText={lt('AddProduct')} title={lt('AddProduct')}>
            <FormSelectField name={AddProductFormCardFormKeys.productId} label={lt('Product')} required>
                {productOptions.map(({ label, secondaryText, value }) => (
                    <DropdownItem key={value} value={value} label={label} description={secondaryText} />
                ))}
            </FormSelectField>
            <FormStepperField name={AddProductFormCardFormKeys.count} min={1} step={1} label={lt('Count')} required />
            <ConsumedDateTimeField serviceProducts={serviceProducts} reservations={reservations} />
        </FormLayout>
    );
};

const ConsumedDateTimeField = ({
    serviceProducts,
    reservations,
}: {
    serviceProducts: Product[];
    reservations: Reservation[];
}) => {
    const selectedProductId = useWatch<AddProductFormCardFormValues>({ name: AddProductFormCardFormKeys.productId });
    const { setValue, getValues } = useFormContext<AddProductFormCardFormValues>();
    const isDateTimeVisible = useMemo(
        () => checkIsDateTimeVisible(selectedProductId, serviceProducts),
        [selectedProductId, serviceProducts]
    );

    const appClockService = useAppClockService();

    // Find the minimum startUtc date from reservations
    // If minimum date is more than now, use now. Otherwise use the minimum date
    const defaultConsumedUTC = useMemo(() => {
        if (!reservations || reservations.length === 0) {
            return null;
        }

        const minStartUtcString = reservations.reduce((min, reservation) => {
            const current = DateTimeUtcService.parse(reservation.startUtc);
            return current.millis < min.millis ? current : min;
        }, DateTimeUtcService.parse(reservations[0].startUtc));

        const now = appClockService.getAppNowUtc();

        // If minimum date is more than now, use now. Otherwise use the minimum date
        return DateTimeUtcService.isGreater(now, minStartUtcString) ? null : minStartUtcString.value;
    }, [appClockService, reservations]);

    const lastSetValueRef = useRef<string | null | undefined>(undefined);
    useEffect(() => {
        // Reset the last set value when the field is not visible
        if (!isDateTimeVisible) {
            lastSetValueRef.current = undefined;
            return;
        }
        // DateTimeField does not track the isDirty state. I need to track the last set value manually
        const isDirty = lastSetValueRef.current !== getValues(AddProductFormCardFormKeys.consumedUTC);
        if (!isDirty) {
            const utcValue = defaultConsumedUTC ?? appClockService.getAppNowUtc().value;
            lastSetValueRef.current = utcValue;
            setValue(AddProductFormCardFormKeys.consumedUTC, utcValue);
        }
    }, [isDateTimeVisible, appClockService, defaultConsumedUTC, setValue, getValues]);

    if (!isDateTimeVisible) return null;

    return (
        <DateTimeField
            name={AddProductFormCardFormKeys.consumedUTC}
            label={lt('ConsumptionDateAndTime')}
            required
            shouldUnregister
        />
    );
};
